import {
  Body1,
  Button,
  Caption1,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Spinner,
  Subtitle1,
  tokens,
} from "@fluentui/react-components";
import { type FormEvent, useRef, useState } from "react";
import { ApiError, type AuthChallenge, auth, type TwoFactorSetup } from "../api/client";
import { baeTypography } from "../theme/baeTheme";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: tokens.spacingHorizontalXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    width: "100%",
    maxWidth: "23rem",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
  },
  brand: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS },
  mark: {
    display: "inline-grid",
    placeItems: "center",
    width: "28px",
    height: "28px",
    borderRadius: tokens.borderRadiusSmall,
    // The electric blue at full strength, on a large shape where it reads.
    backgroundColor: tokens.colorCompoundBrandStroke,
    color: "#FFFFFF",
    fontFamily: baeTypography.fontFamilyDisplay,
    fontSize: "14px",
    fontWeight: 700,
  },
  wordmark: {
    fontFamily: baeTypography.fontFamilyDisplay,
    fontSize: "16px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    margin: 0,
  },
  heading: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.02em" },
  form: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  footer: { color: tokens.colorNeutralForeground3 },
  qr: {
    display: "grid",
    placeItems: "center",
    padding: tokens.spacingHorizontalM,
    // White ground regardless of theme: a scanner needs the contrast, not the palette.
    backgroundColor: "#FFFFFF",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  key: {
    fontFamily: baeTypography.fontFamilyMonospace,
    fontSize: "13px",
    letterSpacing: "0.04em",
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    textAlign: "center",
  },
});

type Step =
  | { kind: "credentials" }
  | { kind: "setPassword"; userId: string; message: string }
  | { kind: "twoFactor"; userId: string; message: string }
  | { kind: "enrolTwoFactor"; userId: string; message: string; setup: TwoFactorSetup | null };

type Props = { onSignedIn: () => void };

const digitsOnly = (value: string) => value.replace(/\D/g, "").slice(0, 8);

/**
 * Wraps an SVG string as a data URI for an `<img>`.
 *
 * Deliberately not `dangerouslySetInnerHTML`. Browsers disable scripting in SVG loaded through
 * `<img>`, so even if the API were ever compromised the worst this can render is a wrong picture
 * -- whereas inlining the markup would execute whatever it contained.
 */
function svgDataUri(svg: string): string {
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/**
 * Headings for the errors worth naming.
 *
 * Keyed on the API's stable code, never the message, so wording can change without silently
 * falling back to "Sign-in failed" (ADR-0009). Anything unlisted gets that fallback, which is the
 * right answer for a wrong password.
 */
const SIGN_IN_ERROR_TITLES: Record<string, string> = {
  NETWORK: "Offline",
  UNEXPECTED: "Something went wrong",
  RATE_LIMITED: "Too many attempts",
  // Identity freezes an account after five failures, which is not a wrong password and should
  // not read like one -- otherwise someone keeps retrying a password that is in fact correct.
  ACCOUNT_LOCKED: "Account locked",
};

/**
 * Turns whatever went wrong into something worth reading.
 *
 * The distinction matters more here than anywhere else in the app. "Could not reach CommerceOS"
 * sends someone to check the server, so it must only be said when that is actually the problem —
 * a failed `fetch` throws `TypeError`, and nothing else does. Anything else reaching this point is
 * a fault in this screen, and saying so is more use than blaming the network.
 */
function toSignInError(caught: unknown): ApiError {
  if (caught instanceof ApiError) {
    return caught;
  }

  if (caught instanceof TypeError) {
    return new ApiError(
      0,
      "NETWORK",
      "Could not reach CommerceOS. Check the API is running, and that you are on the right network.",
    );
  }

  return new ApiError(
    0,
    "UNEXPECTED",
    caught instanceof Error
      ? `Something went wrong in the app: ${caught.message}`
      : "Something went wrong in the app.",
  );
}

/**
 * Sign-in.
 *
 * The API decides what happens next — a session, a forced password change, a second factor, or
 * enrolment — and this screen renders whichever it gets. It branches on the response `code`,
 * never on the message, so wording can change without breaking the flow (ADR-0009).
 */
export function SignInScreen({ onSignedIn }: Props) {
  const styles = useStyles();

  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Enrolment needs the password again, and it must not be re-typed. Held in a ref rather than
  // state so it never becomes a render input or shows up in a component snapshot.
  const currentPassword = useRef("");

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);

    try {
      await work();
    } catch (caught) {
      setError(toSignInError(caught));
    } finally {
      setBusy(false);
    }
  };

  const handleResult = async (result: Awaited<ReturnType<typeof auth.signIn>>) => {
    if (!auth.isChallenge(result)) {
      onSignedIn();
      return;
    }

    const challenge = result as AuthChallenge;

    switch (challenge.code) {
      case "PASSWORD_CHANGE_REQUIRED":
        setStep({ kind: "setPassword", userId: challenge.userId, message: challenge.message });
        break;

      case "TWO_FACTOR_REQUIRED":
        setStep({ kind: "twoFactor", userId: challenge.userId, message: challenge.message });
        break;

      case "TWO_FACTOR_ENROLMENT_REQUIRED": {
        setStep({
          kind: "enrolTwoFactor",
          userId: challenge.userId,
          message: challenge.message,
          setup: null,
        });

        // Fetched straight away. Making the user press another button to see the QR adds a step
        // without adding a decision.
        const setup = await auth.beginTwoFactorSetup(challenge.userId, currentPassword.current);

        setStep({
          kind: "enrolTwoFactor",
          userId: challenge.userId,
          message: challenge.message,
          setup,
        });
        break;
      }
    }
  };

  const submitCredentials = (event: FormEvent) => {
    event.preventDefault();
    currentPassword.current = password;
    void run(async () => handleResult(await auth.signIn(email, password)));
  };

  const submitNewPassword = (event: FormEvent) => {
    event.preventDefault();
    if (step.kind !== "setPassword") return;
    const { userId } = step;
    const chosen = newPassword;

    void run(async () => {
      const result = await auth.setPassword(userId, password, chosen);
      // The new password is what authenticates any enrolment that follows.
      currentPassword.current = chosen;
      await handleResult(result);
    });
  };

  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    if (step.kind !== "twoFactor") return;
    const { userId } = step;
    void run(async () => handleResult(await auth.twoFactor(userId, code)));
  };

  const submitEnrolment = (event: FormEvent) => {
    event.preventDefault();
    if (step.kind !== "enrolTwoFactor") return;
    const { userId } = step;
    void run(async () =>
      handleResult(await auth.enableTwoFactor(userId, currentPassword.current, code)),
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.mark}>B</span>
          <h1 className={styles.wordmark}>BAE CommerceOS</h1>
        </div>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {SIGN_IN_ERROR_TITLES[error.code] ?? "Sign-in failed"}
              </MessageBarTitle>
              {error.message}
            </MessageBarBody>
          </MessageBar>
        )}

        {step.kind === "credentials" && (
          <>
            <Subtitle1 className={styles.heading} as="h2">
              Sign in
            </Subtitle1>

            <form className={styles.form} onSubmit={submitCredentials}>
              <Field label="Email" required>
                <Input
                  type="email"
                  value={email}
                  autoComplete="username"
                  onChange={(_, data) => setEmail(data.value)}
                />
              </Field>

              <Field
                label="Password"
                required
                validationMessage={error?.fieldError("password")}
                validationState={error?.fieldError("password") ? "error" : "none"}
              >
                <Input
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(_, data) => setPassword(data.value)}
                />
              </Field>

              <Button appearance="primary" type="submit" disabled={busy || !email || !password}>
                {busy ? <Spinner size="tiny" /> : "Sign in"}
              </Button>
            </form>
          </>
        )}

        {step.kind === "setPassword" && (
          <>
            <Subtitle1 className={styles.heading} as="h2">
              Choose a password
            </Subtitle1>
            <Body1>{step.message}</Body1>

            <form className={styles.form} onSubmit={submitNewPassword}>
              <Field
                label="New password"
                required
                hint="At least 12 characters. Length matters more than symbols."
                validationMessage={error?.fieldError("newPassword")}
                validationState={error?.fieldError("newPassword") ? "error" : "none"}
              >
                <Input
                  type="password"
                  value={newPassword}
                  autoComplete="new-password"
                  onChange={(_, data) => setNewPassword(data.value)}
                />
              </Field>

              <Button appearance="primary" type="submit" disabled={busy || newPassword.length < 12}>
                {busy ? <Spinner size="tiny" /> : "Set password and continue"}
              </Button>
            </form>
          </>
        )}

        {step.kind === "twoFactor" && (
          <>
            <Subtitle1 className={styles.heading} as="h2">
              Enter your code
            </Subtitle1>
            <Body1>{step.message}</Body1>

            <form className={styles.form} onSubmit={submitCode}>
              <Field label="Authenticator code" required>
                <Input
                  value={code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(_, data) => setCode(digitsOnly(data.value))}
                />
              </Field>

              <Button appearance="primary" type="submit" disabled={busy || code.length < 6}>
                {busy ? <Spinner size="tiny" /> : "Continue"}
              </Button>
            </form>
          </>
        )}

        {step.kind === "enrolTwoFactor" && (
          <>
            <Subtitle1 className={styles.heading} as="h2">
              Add an authenticator
            </Subtitle1>
            <Body1>{step.message}</Body1>

            {step.setup ? (
              <>
                <div className={styles.qr}>
                  <img
                    src={svgDataUri(step.setup.qrCodeSvg)}
                    alt="Scan this with your authenticator app"
                    width={200}
                    height={200}
                  />
                </div>

                <Caption1>
                  Scan with Microsoft Authenticator, Google Authenticator or 1Password. If you
                  cannot scan, enter this key by hand:
                </Caption1>
                <code className={styles.key}>{step.setup.sharedKey}</code>

                <form className={styles.form} onSubmit={submitEnrolment}>
                  <Field label="Code from the app" required>
                    <Input
                      value={code}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      onChange={(_, data) => setCode(digitsOnly(data.value))}
                    />
                  </Field>

                  <Button appearance="primary" type="submit" disabled={busy || code.length < 6}>
                    {busy ? <Spinner size="tiny" /> : "Turn on and sign in"}
                  </Button>
                </form>
              </>
            ) : (
              <Spinner label="Preparing your authenticator" />
            )}

            <Link onClick={() => setStep({ kind: "credentials" })}>Back to sign in</Link>
          </>
        )}

        <Caption1 className={styles.footer}>
          BAE Infotech · CommerceOS
          {error?.correlationId ? ` · ref ${error.correlationId.slice(0, 16)}` : ""}
        </Caption1>
      </div>
    </div>
  );
}
